import React, { useEffect, useState } from 'react';

import { Video } from './VideoComponent';
import { WindowWithCloudinary } from './windowWithCloudinary';

interface CloudinaryTransformationOptions {
  aspectRatio?: string;
  crop?: string;
  width?: string;
  height?: string;
  gravity?: string;
  format?: string;
  dpr?: string;
  quality?: string;
  startOffset?: number;
  transformation?: string | CloudinaryTransformationOptions;
}

export interface CloudinaryVideoConfigProps {
  preload?: string;
  playsInline?: boolean;
  muted?: boolean;
  transformation?: CloudinaryTransformationOptions;
  autoplay: boolean;
  loop: boolean;
}

export interface CloudinaryVideoConfig extends CloudinaryVideoConfigProps {
  fluid: boolean;
  colors: {
    accent: string;
  };
  controls: boolean;
  logoOnclickUrl: string;
  logoImageUrl: string;
  posterOptions?: {
    publicId: string;
  };
}

export interface CloudinaryVideoProps {
  publicId: string;
  playerId: string;
  tenant: string;
  videoConfig: CloudinaryVideoConfigProps;
  posterUrl?: string;
  altText?: string;
  cloudinaryUrl?: string;
}

export const CloudinaryVideo: React.FC<CloudinaryVideoProps> = (props) => {
  const { publicId, playerId, videoConfig, tenant, posterUrl, altText, cloudinaryUrl } = props;

  const [isVideoInstantiated, setIsVideoInstantiated] = useState<boolean>(false);
  const [isCloudinaryAvailable, setIsCloudinaryAvailable] = useState<boolean>(false);

  useEffect(() => {
    const cloudinaryListener = window.setInterval(function () {
      if (
        !isCloudinaryAvailable &&
        (window as WindowWithCloudinary).cloudinary &&
        (window as WindowWithCloudinary).cloudinary.Cloudinary
      ) {
        setIsCloudinaryAvailable(true);
        window.clearInterval(cloudinaryListener);
      }
    }, 100);
    return function () {
      window.clearInterval(cloudinaryListener);
    };
  }, []);

  const classNamePlayerId = `cld-video-id-class-${playerId}`;

  useEffect(() => {
    if (isCloudinaryAvailable && !isVideoInstantiated) {
      const videoConfigs: CloudinaryVideoConfig = {
        ...videoConfig,
        fluid: true,
        colors: {
          accent: '#0099e0', // $accent-blue
        },
        controls: true,
        logoOnclickUrl: window.location.origin,
        logoImageUrl:
          'https://cms.cloudinary.vpsvc.com/image/upload/dpr_auto,q_auto/v1560966964/logomark.png',
        ...(posterUrl && {
          posterOptions: {
            publicId: posterUrl,
          },
        }),
      };

      try {
        (window as WindowWithCloudinary).cloudinary.videoPlayers(`.${classNamePlayerId}`, {
          ...videoConfigs,
          cloud_name: tenant.toLowerCase(),
          secure: true,
          cloudinaryAnalytics: true,
        });
      } catch (error: unknown) {
        handleVideoPlayerError(error);
      }

      setIsVideoInstantiated(true);
    }
  }, [isCloudinaryAvailable]);

  return playerId && publicId ? (
    <>
      <Video
        playerId={playerId}
        classNamePlayerId={classNamePlayerId}
        publicId={publicId}
        cloudinaryUrl={cloudinaryUrl}
        altText={altText}
      />
    </>
  ) : (
    <></>
  );
};

function handleVideoPlayerError(e: unknown) {
  const knownErrorVideoPlayerAlreadyInitialized = 'Element is not a video tag';
  if (!(e as Error)?.message?.includes(knownErrorVideoPlayerAlreadyInitialized)) throw e;
}
